import {
  IlogisticServiceTrelloCardData,
  IlogisticServiceDetails,
  LOCATION_PLACE_TYPE,
  LOGISTIC_TYPES,
  IAddExternalProductsToLogisticService,
  IAddExternalProductReference,
  IProductReference,
  DTOupdateLogisticServiceStatus,
  LOGISTIC_SERVICE_STATUS_NAME,
  IlogisticServiceDetailsToolsData
} from '../types/services';
import { PRODUCT_CONDITION } from '../types/products';

/**
 * Determina si un servicio logístico es un offboarding con destino a bodega
 * @param logisticServiceData - Datos del servicio logístico
 * @returns true si es offboarding con destino a bodega
 */
export const checkIfOffboardingToWarehouse = (logisticServiceData?: IlogisticServiceTrelloCardData): boolean => {
  if (!logisticServiceData) return false;
  return (
    logisticServiceData.logisticType === LOGISTIC_TYPES.OFFBOARDING &&
    logisticServiceData.destination?.place === LOCATION_PLACE_TYPE.NUDOS
  );
};

/**
 * Determina si un servicio logístico es un onboarding desde bodega
 * @param logisticServiceData - Datos del servicio logístico
 * @returns true si es onboarding desde bodega
 */
export const checkIfOnboardingFromWarehouse = (logisticServiceData?: IlogisticServiceTrelloCardData): boolean => {
  if (!logisticServiceData) return false;
  return (
    logisticServiceData.logisticType === LOGISTIC_TYPES.ONBOARDING &&
    logisticServiceData.origin?.place === LOCATION_PLACE_TYPE.NUDOS
  );
};

/**
 * Construye el DTO para agregar productos externos a un servicio logístico
 * @param serviceDetails - Detalles del servicio logístico
 * @param selectedTools - Lista de herramientas seleccionadas
 * @param toolsSerials - Mapa de índices a seriales de las herramientas
 * @returns DTO para agregar productos externos o null si faltan datos requeridos
 */
export const buildAddExternalProductsDTO = (
  serviceDetails: IlogisticServiceDetails,
  selectedTools: IProductReference[],
  toolsSerials: { [key: number]: string }
): IAddExternalProductsToLogisticService | null => {
  const organizationId = serviceDetails?.organization?.organizationId;
  const logisticServiceId = serviceDetails?.id;

  if (!organizationId || !logisticServiceId) {
    return null;
  }

  const user = serviceDetails.collectionInfo?.user;
  const countryId =
    serviceDetails.originLocation?.country?.id ||
    serviceDetails.collectionInfo?.delivererCountryPhone?.id ||
    serviceDetails.organization?.country?.id;

  if (!countryId) {
    return null;
  }

  const originPlace = serviceDetails.originLocation?.place;
  const place =
    originPlace === LOCATION_PLACE_TYPE.USER
      ? LOCATION_PLACE_TYPE.USER
      : originPlace === LOCATION_PLACE_TYPE.OFFICE
      ? LOCATION_PLACE_TYPE.OFFICE
      : null;

  const optionId = place === LOCATION_PLACE_TYPE.OFFICE ? 1 : place === LOCATION_PLACE_TYPE.USER ? 3 : null;

  if (!optionId) {
    return null;
  }

  const assigneeInfo =
    user?.userId && user?.firstName && user?.lastName
      ? {
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName
        }
      : undefined;

  const references: IAddExternalProductReference[] = selectedTools?.map((tool, index) => {
    const serial = toolsSerials[index];
    if (!serial) {
      throw new Error(`El serial es requerido para la herramienta ${tool.name}`);
    }

    const individualToolData = {
      serial,
      productCondition: PRODUCT_CONDITION.USED_GOOD as string,
      warranty: '',
      optionId,
      locationInfo: {
        place,
        placeName: null
      },
      countryId,
      ...(assigneeInfo && { userInfo: assigneeInfo })
    };

    return {
      id: tool.id,
      sku: tool.sku,
      name: tool.name,
      category: tool.category || '',
      quantity: 1,
      individualToolsData: [individualToolData]
    };
  });

  return {
    organizationId,
    logisticServiceId,
    references
  };
};

interface DeliveredTool {
  tool: IlogisticServiceDetailsToolsData;
  originalIndex: number;
}

/**
 * Extrae los IDs de productos de las herramientas no entregadas
 * @param unselectedTools - Array de herramientas no seleccionadas
 * @returns Array de IDs de productos no entregados
 */
export const extractUndeliveredProductIds = (unselectedTools: DeliveredTool[]): number[] => {
  return unselectedTools
    .map(({ tool }) => tool?.productId)
    .filter((productId): productId is number => productId !== null && productId !== undefined);
};

/**
 * Construye el payload para marcar un servicio logístico como entregado
 * @param userId - ID del usuario que realiza la acción
 * @param deliveryDate - Fecha de entrega
 * @param undeliveredProductIds - IDs de productos no entregados (opcional)
 * @returns Payload para actualizar el estado del servicio logístico
 */
export const buildMarkAsDeliveredPayload = (
  userId: number,
  deliveryDate: string,
  undeliveredProductIds?: number[]
): DTOupdateLogisticServiceStatus => {
  return {
    userId,
    status: LOGISTIC_SERVICE_STATUS_NAME.DELIVERED,
    delivered: {
      deliveryDate,
      ...(undeliveredProductIds && undeliveredProductIds?.length > 0 && { notDeliveredProductIds: undeliveredProductIds })
    }
  };
};
